/**
 * EDGE Worker API client.
 *
 * All requests carry the admin bearer token (Phase 1 single-user auth).
 * In dev, paths are relative and Vite proxies /api to the local Worker.
 * In prod, the full workers.dev URL is read from VITE_API_URL.
 */
import type {
  ActivityEvent,
  Alert,
  DealDetail,
  DealFieldsResponse,
  DealFieldMetaResponse,
  DealNote,
  LookupResult,
  DealSummary,
  TaskItem,
  WorkDriveListResponse,
  WdFile,
} from "@edge/shared";
import { ADMIN_TOKEN, apiUrl } from "./config";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown,
  ) {
    super(`API ${status}: ${code}`);
  }
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (ADMIN_TOKEN) headers.set("Authorization", `Bearer ${ADMIN_TOKEN}`);
  headers.set("Accept", "application/json");
  const res = await fetch(apiUrl(path), { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const code =
      typeof body === "object" && body && "error" in body
        ? String((body as { error: unknown }).error)
        : res.statusText;
    throw new ApiError(res.status, code, body);
  }
  // 204 = no content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ============================================================
// Endpoints — kept narrow on purpose. Add as cockpit needs.
// ============================================================
export const fetchDealsStrip = () => api<DealSummary[]>("/api/deals/strip");
export const fetchCrmTasks = () => api<TaskItem[]>("/api/tasks/crm");

/**
 * Create a new Zoho Task. `deal_id` links the task to a Zoho Deal record.
 */
export const createCrmTask = (body: {
  subject: string;
  description?: string;
  due_date?: string;
  deal_id?: string;
  priority?: "Highest" | "High" | "Normal" | "Low" | "Lowest";
}) =>
  api<{ ok: true; id: string | null }>("/api/tasks/crm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/**
 * Write back to Zoho CRM. Only the explicit fields in `body` are sent —
 * unrelated Zoho fields stay untouched.
 */
export const updateCrmTask = (
  id: string,
  body: {
    status?: "Not Started" | "In Progress" | "Completed" | "Waiting on someone else" | "Deferred";
    due_date?: string | null;
    subject?: string;
    priority?: "Highest" | "High" | "Normal" | "Low" | "Lowest";
    description?: string;
  },
) =>
  api<{ ok: true; applied: Record<string, string | null> }>(
    `/api/tasks/crm/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
export interface ActivityResponse {
  events: ActivityEvent[];
  /** Count of events suppressed by noise filter. Drives the banner. */
  noiseSuppressed: number;
  /** Count of live (non-noise) events available before trim. */
  liveCount?: number;
  note?: string;
}
export const fetchActivity = (
  opts: { limit?: number; includeNoise?: boolean } = {},
) => {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.includeNoise) params.set("include_noise", "true");
  const qs = params.toString();
  return api<ActivityResponse>(`/api/activity${qs ? "?" + qs : ""}`);
};

// ── Email reply (Piece 2, Stage 2) — the single gated send path ──
export interface ReplyResult {
  ok: true;
  reply_all: boolean;
  redirected: boolean;
  sent_to: string[];
  intended_to: string[];
  intended_cc: string[];
  deduped?: boolean;
}
export const replyEmail = (
  id: string,
  body: { body: string; reply_all?: boolean; idempotency_key?: string },
) =>
  api<ReplyResult>(`/api/email/${encodeURIComponent(id)}/reply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/** EM-6 #3 — forward a message (original body + attachments included by Graph's
 *  createForward) to user-picked recipients, with an optional comment. */
export const forwardEmail = (
  id: string,
  body: {
    to: Array<{ email: string; name?: string }>;
    cc?: Array<{ email: string; name?: string }>;
    comment?: string;
    /** EM-6 #3 — the edited subject + forwarded body (user can trim). */
    subject?: string;
    forwarded_body?: string;
    idempotency_key?: string;
  },
) =>
  api<ReplyResult>(`/api/email/${encodeURIComponent(id)}/forward`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

// QW-5: toggle a message read/unread in the mailbox (read=false re-flags it as
// unread — used as a "deal with this later" marker).
export const setEmailRead = (id: string, read: boolean) =>
  api<{ ok: boolean; isRead: boolean }>(`/api/email/${encodeURIComponent(id)}/read`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ read }),
  });

// QW-6: move a message to Deleted Items (soft delete, recoverable from Outlook).
export const deleteEmail = (id: string) =>
  api<{ ok: boolean; moved_to: string }>(`/api/email/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export interface SendGuard {
  active: boolean;
  redirect_to: string | null;
}
export const fetchSendGuard = () => api<SendGuard>("/api/email/send-guard");

// ── AI reply drafting (Piece 2, Stage 3) — 3 modes, output is plain text only ──
export const draftEmail = (req: {
  mode: "generate" | "polish" | "edit";
  /** "reply" (default) uses thread_context; "new" uses compose_context + brief. */
  kind?: "reply" | "new";
  thread_context?: { subject?: string; from?: string; body?: string };
  compose_context?: { to?: string; subject?: string };
  current_draft?: string;
  instruction?: string;
  /** EM-1: pull deal-document content into the draft (instruction = the query). */
  use_docs?: boolean;
  deal_id?: string;
}) =>
  api<{
    draft: string;
    /** EM-1 transparency: docs the drafter used / looked at, or no match. */
    docs?: { used: string[]; looked_at: string[]; no_match: boolean };
  }>("/api/email/draft", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });

// ── New-message compose (Piece 2, Compose) — gated chokepoint + contact picker ──
export interface ContactHit {
  name: string;
  email: string;
  source: "borrower" | "lender";
  sublabel?: string;
}
export const searchContacts = (q: string) =>
  api<{ contacts: ContactHit[] }>(
    `/api/email/contacts/search?q=${encodeURIComponent(q)}`,
  );
export interface SendResult {
  ok: boolean;
  redirected: boolean;
  sent_to: string[];
  intended_to: string[];
  intended_cc: string[];
  deduped?: boolean;
}
export const sendEmail = (req: {
  to: Array<{ email: string; name?: string }>;
  cc: Array<{ email: string; name?: string }>;
  subject: string;
  body: string;
  idempotency_key: string;
  /** WD-2.1 — WorkDrive files to attach (shared EM-3 path). Carrying name +
   *  sizeBytes lets the server skip metadata calls (fewer subrequests). */
  workdrive_files?: Array<{ fileId: string; name: string; sizeBytes: number }>;
  deal_id?: string;
  /** EM-6 #2 — tag this new outbound thread to a deal: writes a durable manual
   *  association so the thread + replies flow into that deal's Email tab. */
  tag_deal_id?: string;
  tag_deal_name?: string;
}) =>
  api<SendResult>("/api/email/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });

// ── Thread→deal association (Piece 2, Stage 4) — the banner's backend ──
export interface ThreadAssociation {
  linked: boolean;
  source?: "auto" | "manual";
  deal_id?: string;
  deal_name?: string | null;
}
export interface DealSearchHit {
  id: string;
  name: string;
  stage: string;
  closed: boolean;
}
// ── Full conversation thread (workspace conversation view) ──
export interface ThreadMessage {
  id: string;
  direction: "incoming" | "outgoing";
  fromName: string;
  fromEmail: string;
  to: string[];
  receivedAt: string;
  body: string;
  hasAttachments: boolean;
  webLink: string | null;
}
export interface EmailThread {
  conversation_id: string;
  subject: string;
  count: number;
  messages: ThreadMessage[];
}
export const fetchEmailThread = (conversationId: string) =>
  api<EmailThread>(
    `/api/email/thread?conversation_id=${encodeURIComponent(conversationId)}`,
  );

export const fetchAssociation = (conversationId: string) =>
  api<ThreadAssociation>(
    `/api/email/association?conversation_id=${encodeURIComponent(conversationId)}`,
  );
export const associateThread = (req: {
  conversation_id: string;
  deal_id: string;
  deal_name?: string;
}) =>
  api<{ ok: boolean; source: string; deal_id: string; deal_name: string | null }>(
    "/api/email/associate",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
  );
export const removeAssociation = (conversationId: string) =>
  api<{ ok: boolean }>("/api/email/associate/remove", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId }),
  });
export const searchDeals = (q: string, includeClosed: boolean) =>
  api<{ deals: DealSearchHit[] }>(
    `/api/deals/search?q=${encodeURIComponent(q)}&include_closed=${includeClosed ? "true" : "false"}`,
  );

// ── Email workspace (Piece 2) — whole unfiltered inbox ──
export interface InboxLite {
  id: string;
  conversationId: string;
  from: { name: string; email: string };
  to: Array<{ name: string; email: string }>;
  subject: string;
  snippet: string;
  receivedDateTime: string;
  isRead: boolean;
  hasAttachments: boolean;
  direction: "incoming" | "outgoing";
}
export interface InboxPage {
  emails: InboxLite[];
  nextCursor: string | null;
}
export const fetchInbox = (
  opts: { search?: string; cursor?: string; limit?: number } = {},
) => {
  const p = new URLSearchParams();
  if (opts.search) p.set("search", opts.search);
  if (opts.cursor) p.set("cursor", opts.cursor);
  if (opts.limit) p.set("limit", String(opts.limit));
  const qs = p.toString();
  return api<InboxPage>(`/api/email/inbox${qs ? "?" + qs : ""}`);
};

/** One-click "Not noise" — permanently force a sender real (allow-list). */
export const allowSender = (email: string) =>
  api<{ ok: true; email: string }>("/api/activity/allow-sender", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
/** Reciprocal — suppress a sender (curated noise). */
export const markNoise = (email: string) =>
  api<{ ok: true; email: string }>("/api/activity/mark-noise", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

import type {
  DealInboxResponse,
  EmailAttachmentMeta,
  EmailParty,
} from "./types/email";

export interface EmailDetail {
  id: string;
  /** Phase 1 EMAIL tab — Graph conversationId (null for legacy callers). */
  conversationId?: string | null;
  fromName: string;
  fromEmail: string;
  toRecipients: EmailParty[];
  /** Phase 1 EMAIL tab — CC line. Optional for backward-compatible callers. */
  ccRecipients?: EmailParty[];
  subject: string;
  body: string;
  receivedAt: string;
  isRead: boolean;
  hasAttachments: boolean;
  /** Phase 1 EMAIL tab — downloadable attachment metadata. */
  attachments?: EmailAttachmentMeta[];
  webLink: string | null;
}
export const fetchEmail = (id: string) =>
  api<EmailDetail>(`/api/email/${encodeURIComponent(id)}`);

// ─── EMAIL tab (Phase 1 — per-deal inbox, read-only) ──────────────────────────

/** Matched inbox for a deal (Mode 1 contact-address + Mode 2 thread tracking). */
export const fetchDealInbox = (dealId: string) =>
  api<DealInboxResponse>(
    `/api/email/deal/${encodeURIComponent(dealId)}/inbox`,
  );

/** Bust the 5-min inbox KV cache, then the caller refetches. */
export const refreshDealInbox = (dealId: string) =>
  api<{ ok: true; cleared: string }>(
    `/api/email/refresh/${encodeURIComponent(dealId)}`,
    { method: "POST" },
  );

/**
 * Fetch an attachment's bytes through the authenticated download proxy. The
 * SPA can't put a Bearer header on an <a download>, so we pull the blob here
 * and the modal triggers the save client-side.
 */
export const fetchEmailAttachmentBlob = async (
  messageId: string,
  attachmentId: string,
): Promise<Blob> => {
  const headers = new Headers({ Accept: "application/octet-stream" });
  if (ADMIN_TOKEN) headers.set("Authorization", `Bearer ${ADMIN_TOKEN}`);
  const res = await fetch(
    apiUrl(
      `/api/email/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
    ),
    { headers },
  );
  if (!res.ok) {
    throw new ApiError(res.status, "attachment_download_failed", null);
  }
  return res.blob();
};

/**
 * EM-4 — save an inbound email attachment into a deal's WorkDrive folder. Bytes
 * are pulled from Graph + uploaded server-side (never via the browser).
 * folderId omitted → the deal's root folder.
 */
export const saveEmailAttachmentToWorkdrive = (
  messageId: string,
  attachmentId: string,
  dealId: string,
  folderId?: string,
) =>
  api<{ ok: true; folder_id: string; file: WdFile }>(
    `/api/email/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}/save-to-workdrive`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deal_id: dealId, folder_id: folderId }),
    },
  );

// ─── Submissions (S.1+S.2+S.3) ────────────────────────────────────────────
import type {
  SubmissionSummary,
  SubmissionDetail,
  SubmissionStatus,
  SubmissionTouchDirection,
  LenderAccount,
  LenderContact,
} from "@edge/shared";

export const fetchSubmissions = (dealId: string) =>
  api<{ submissions: SubmissionSummary[] }>(
    `/api/submissions?deal_id=${encodeURIComponent(dealId)}`,
  );

export const fetchSubmissionDetail = (id: string) =>
  api<SubmissionDetail>(`/api/submissions/${encodeURIComponent(id)}`);

export const fetchLenders = () =>
  api<{ lenders: LenderAccount[]; cached: boolean }>(`/api/lenders`);

export const fetchLenderContacts = (lenderId: string) =>
  api<{ contacts: LenderContact[]; cached: boolean }>(
    `/api/lenders/${encodeURIComponent(lenderId)}/contacts`,
  );

// ─── Writes ───────────────────────────────────────────────────────────────

export interface CreateSubmissionInput {
  deal_id: string;
  lender_account_id: string;
  lender_name: string;
  contact_id?: string | null;
  contact_name?: string | null;
  contact_role?: string | null;
  status?: SubmissionStatus;
  pitched_at?: string;
}
export const createSubmission = (body: CreateSubmissionInput) =>
  api<{ id: string; created: true }>(`/api/submissions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export interface PatchSubmissionInput {
  status?: SubmissionStatus;
  decline_reason?: string | null;
  backup_reason?: string | null;
  contact_id?: string | null;
  contact_name?: string | null;
  contact_role?: string | null;
  archived_at?: string | null;
  /** Optional atomic touch inserted alongside the patch. */
  touch?: { body: string; direction?: SubmissionTouchDirection };
}
export const patchSubmission = (id: string, body: PatchSubmissionInput) =>
  api<{ id: string; updated: true }>(
    `/api/submissions/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

export const archiveSubmission = (id: string) =>
  api<{ id: string; archived: true }>(
    `/api/submissions/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );

export interface CreateTouchInput {
  direction: SubmissionTouchDirection;
  body: string;
  status_at_time?: SubmissionStatus;
  occurred_at?: string;
}
export const createTouch = (submissionId: string, body: CreateTouchInput) =>
  api<{ id: string; created: true }>(
    `/api/submissions/${encodeURIComponent(submissionId)}/touches`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

export const archiveTouch = (submissionId: string, touchId: string) =>
  api<{ id: string; archived: true }>(
    `/api/submissions/${encodeURIComponent(submissionId)}/touches/${encodeURIComponent(touchId)}`,
    { method: "DELETE" },
  );

export interface CreateItemInput {
  label: string;
  notes?: string | null;
  /** Deal name for the AI-generated Zoho task subject. Optional — worker falls
   *  back to a hand-rolled subject if omitted. */
  deal_name?: string;
}
export interface CreateItemResponse {
  id: string;
  created: true;
  zoho_task_id: string | null;
  /** True when the auto-link to a Zoho CRM task failed. The item still saved;
   *  the UI surfaces a ⚠ retry icon. */
  zoho_link_warning: boolean;
  zoho_link_error?: string;
}
export const createItem = (submissionId: string, body: CreateItemInput) =>
  api<CreateItemResponse>(
    `/api/submissions/${encodeURIComponent(submissionId)}/items`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

/**
 * Retry the AI-subject + Zoho-task-create flow for an item that has no link
 * yet (warning icon click). Returns the new task id on success; throws an
 * ApiError on failure (worker responds 502 with detail).
 */
export const retryItemZohoLink = (
  submissionId: string,
  itemId: string,
  body: { deal_name?: string } = {},
) =>
  api<{ ok: true; zoho_task_id: string; already_linked?: boolean }>(
    `/api/submissions/${encodeURIComponent(submissionId)}/items/${encodeURIComponent(itemId)}/retry-zoho-link`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

export interface PatchItemInput {
  sent_at?: string | null;
  label?: string;
  notes?: string | null;
}
export const patchItem = (
  submissionId: string,
  itemId: string,
  body: PatchItemInput,
) =>
  api<{ id: string; updated: true }>(
    `/api/submissions/${encodeURIComponent(submissionId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

export const archiveItem = (submissionId: string, itemId: string) =>
  api<{ id: string; archived: true }>(
    `/api/submissions/${encodeURIComponent(submissionId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
  );
/**
 * Fetch the latest Watchtower classification. By default reads the worker's
 * 30-min KV cache; pass `refresh: true` to force a fresh classifier run
 * (the cockpit's manual refresh button uses this).
 */
export const fetchWatchtower = (opts: { refresh?: boolean } = {}) => {
  const qs = opts.refresh ? "?refresh=1" : "";
  return api<{ alerts: Alert[]; note?: string; cached?: boolean }>(
    `/api/watchtower${qs}`,
  );
};

/**
 * Dismiss an active Watchtower alert. `kind` distinguishes the two user
 * intents: `solved` (the alert was real and I handled it) vs `removed` (the
 * alert was irrelevant / misread the situation). Phase 1 ships without a
 * note field; Phase 2 adds the with-feedback dropdown variant.
 */
export const dismissWatchtowerAlert = (
  id: string,
  body: { kind: "solved" | "removed"; note?: string },
) =>
  api<{ ok: true; status: string }>(
    `/api/watchtower/alerts/${encodeURIComponent(id)}/dismiss`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
export const fetchDealDetail = (id: string) =>
  api<DealDetail>(`/api/deals/${encodeURIComponent(id)}`);
export const fetchDealNotes = (id: string) =>
  api<DealNote[]>(`/api/deals/${encodeURIComponent(id)}/notes`);

// WD-2 — a deal's WorkDrive folder listing (optionally a subfolder via folderId).
export const fetchDealWorkdrive = (id: string, folderId?: string) =>
  api<WorkDriveListResponse>(
    `/api/deals/${encodeURIComponent(id)}/workdrive${folderId ? `?folder=${encodeURIComponent(folderId)}` : ""}`,
  );

/**
 * WD-2.1 — pull a WorkDrive file's bytes through the authenticated proxy. Used
 * by the preview modal (inline) and the download button. `download:true` is a
 * cosmetic hint for the server's Content-Disposition; the client triggers the
 * actual save from the blob either way. Throws ApiError(413) on oversize files.
 */
export const fetchDealWorkdriveFileBlob = async (
  dealId: string,
  fileId: string,
  download = false,
): Promise<Blob> => {
  const headers = new Headers({ Accept: "application/octet-stream" });
  if (ADMIN_TOKEN) headers.set("Authorization", `Bearer ${ADMIN_TOKEN}`);
  const res = await fetch(
    apiUrl(
      `/api/deals/${encodeURIComponent(dealId)}/workdrive/file/${encodeURIComponent(fileId)}/content${download ? "?dl=1" : ""}`,
    ),
    { headers },
  );
  if (!res.ok) {
    let code = "workdrive_download_failed";
    try {
      const b = (await res.json()) as { error?: string };
      if (b?.error) code = b.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code, null);
  }
  return res.blob();
};

/**
 * WD-2.1 — upload file(s) into the WorkDrive folder currently being viewed
 * (folderId = the deepest breadcrumb id). Additive/safe. Returns the created
 * WdFiles so the tab can refresh.
 */
export const uploadDealWorkdriveFiles = async (
  dealId: string,
  folderId: string,
  files: File[],
): Promise<{ ok: true; folder_id: string; uploaded: WdFile[] }> => {
  const form = new FormData();
  for (const f of files) form.append("file", f, f.name);
  const headers = new Headers();
  if (ADMIN_TOKEN) headers.set("Authorization", `Bearer ${ADMIN_TOKEN}`);
  const res = await fetch(
    apiUrl(
      `/api/deals/${encodeURIComponent(dealId)}/workdrive/upload?folder=${encodeURIComponent(folderId)}`,
    ),
    { method: "POST", headers, body: form },
  );
  if (!res.ok) {
    let code = "workdrive_upload_failed";
    try {
      const b = (await res.json()) as { error?: string };
      if (b?.error) code = b.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code, null);
  }
  return res.json() as Promise<{ ok: true; folder_id: string; uploaded: WdFile[] }>;
};

// FO-0 — Focus Overview + Deal Editor field data.
export const fetchDealFields = (id: string) =>
  api<DealFieldsResponse>(`/api/deals/${encodeURIComponent(id)}/fields`);
/** Live picklist options + missing-field reconcile (deal-independent, cached). */
export const fetchDealFieldMeta = () =>
  api<DealFieldMetaResponse>("/api/deals/field-meta");
/** FO-2 — whitelisted partial write of Deal fields + lookups (Co_Borrower, Lender). */
export const updateDealFields = (
  id: string,
  body: { fields?: Record<string, unknown>; lookups?: Record<string, string | null> },
) =>
  api<{ ok: true; written: string[] }>(`/api/deals/${encodeURIComponent(id)}/fields`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
/** FO-2b/2c — search a Zoho lookup module (Contacts/Accounts) → {id,name}. */
export const searchDealLookup = (module: "Contacts" | "Accounts", q: string) =>
  api<{ ok: true; results: LookupResult[] }>(
    `/api/deals/lookup-search?module=${module}&q=${encodeURIComponent(q)}`,
  );
/** FO-2c — create a new Zoho Contact (min last_name) → {id,name}. */
export const createDealContact = (body: {
  last_name: string;
  first_name?: string;
  email?: string;
  phone?: string;
}) =>
  api<{ ok: true; id: string; name: string }>("/api/deals/contacts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
/**
 * Update whitelisted Zoho Deal fields. Mirrors the `propose_deal_update`
 * tool's accepted fields; worker validates again on its side.
 */
export interface DealUpdateBody {
  loan_amount?: number;
  close_date?: string;
  stage?: string;
  loan_purpose?: string;
  lien_position?: string;
  dscr_requested?: string;
  property_location?: string;
  property_type?: string;
  property_state?: string;
  term_months?: number;
}
export const updateDeal = (id: string, body: DealUpdateBody) =>
  api<{ ok: true; applied: Record<string, unknown> }>(
    `/api/deals/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

export const createDealNote = (
  dealId: string,
  body: { content: string; title?: string },
) =>
  api<{ ok: true; id: string | null }>(
    `/api/deals/${encodeURIComponent(dealId)}/notes`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

// Personal tasks (D1-backed)
export const fetchPersonalTasks = () =>
  api<TaskItem[]>("/api/personal-tasks");
export const fetchCompletedPersonalTasks = (q?: string, limit = 50) => {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("limit", String(limit));
  return api<TaskItem[]>(`/api/personal-tasks/completed?${params}`);
};
export const createPersonalTask = (body: {
  subject: string;
  description?: string;
  due_date?: string;
  priority?: "high" | "low";
  /** Target Todoist project id (My List ↔ Todoist). Omit ⇒ Todoist Inbox. */
  project_id?: string;
}) =>
  api<TaskItem>("/api/personal-tasks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/** Cached Todoist project list — for the create dropdown + badges. */
export interface TodoistProjectLite {
  id: string;
  name: string;
  color: string | null;
  is_inbox: boolean;
}
export const fetchTodoistProjects = () =>
  api<TodoistProjectLite[]>("/api/personal-tasks/projects");
export const createTodoistProject = (name: string) =>
  api<{ id: string; name: string; color: string | null }>(
    "/api/personal-tasks/projects",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    },
  );
export const updatePersonalTask = (
  id: string,
  body: {
    completed?: boolean;
    snooze_days?: number;
    subject?: string;
    description?: string | null;
    /** ISO date string sets the due date; null clears it. */
    due_date?: string | null;
    priority?: "high" | "normal" | "low";
  },
) =>
  api<TaskItem | null>(`/api/personal-tasks/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
export const deletePersonalTask = (id: string) =>
  api<{ ok: true }>(`/api/personal-tasks/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export interface AuthStatus {
  zoho: boolean;
  microsoft: boolean;
  anthropic: boolean;
}
export const fetchAuthStatus = () => api<AuthStatus>("/api/auth/status");

/**
 * Fire-and-forget log of "Noam entered focus mode on this deal". Drives
 * the Watchtower `deal_neglected` rule — every entry counts regardless of
 * trigger (deal-strip click, chat directive, Pipeline card, breadcrumb).
 * Errors are swallowed: a missed log is non-critical.
 */
export const logFocusOpened = (dealId: string, dealName?: string) =>
  api<{ ok: true }>("/api/activity/focus-opened", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deal_id: dealId, deal_name: dealName ?? null }),
  }).catch(() => {
    // best-effort
  });

// ============================================================
// Calendar (Phase C.1)
// ============================================================
export interface CalendarEvent {
  id: string;
  outlookEventId: string | null;
  title: string;
  body: string | null;
  location: string | null;
  startAt: string;
  endAt: string;
  allDay: boolean;
  priority: "hi" | "normal" | "lo";
  dealId: string | null;
  dealName: string | null;
  leadTimeDays: number;
  source: "edge" | "outlook";
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarEventInput {
  title: string;
  body?: string | null;
  location?: string | null;
  startAt: string;
  endAt: string;
  allDay?: boolean;
  priority?: "hi" | "normal" | "lo";
  dealId?: string | null;
  dealName?: string | null;
  leadTimeDays?: number;
}

export const fetchCalendarEvents = (params?: { from?: string; to?: string }) => {
  const q = new URLSearchParams();
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  const qs = q.toString();
  return api<{ events: CalendarEvent[] }>(
    `/api/calendar${qs ? `?${qs}` : ""}`,
  );
};

export const createCalendarEvent = (body: CalendarEventInput) =>
  api<{ event: CalendarEvent }>("/api/calendar", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const updateCalendarEvent = (
  id: string,
  body: Partial<CalendarEventInput>,
) =>
  api<{ event: CalendarEvent }>(`/api/calendar/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const deleteCalendarEvent = (id: string) =>
  api<{ ok: true }>(`/api/calendar/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export interface CalendarSyncResult {
  ok: true;
  processed: number;
  inserted: number;
  updated: number;
  softDeleted: number;
}
export const syncCalendar = () =>
  api<CalendarSyncResult>("/api/calendar/sync", { method: "POST" });

/** UI side-channel emitted by chat-brain tools — mirrors the worker's ToolDirective. */
export type ChatDirective =
  | { type: "navigate"; deal_id: string; deal_name: string }
  | { type: "open_pipeline" }
  | { type: "open_tasks" }
  | { type: "open_calendar" }
  | {
      type: "propose_crm_task_create";
      initial: {
        subject: string;
        description?: string;
        due_date?: string;
        priority?: "Highest" | "High" | "Normal" | "Low" | "Lowest";
        deal_id?: string;
      };
    }
  | {
      type: "propose_deal_update";
      deal_id: string;
      deal_name: string;
      changes: Array<{ field: string; from: string; to: string }>;
      fields: DealUpdateBody;
    }
  | {
      type: "propose_note_create";
      initial: { content: string; deal_id?: string };
    }
  | ProposeClosedLoanDirective;

/** One extracted closed-loan field (value + confidence + source filename). */
export interface ClosedLoanDirectiveField {
  value: string | number | null;
  confidence?: number;
  source?: string;
}
/** Opens ClosedLoanReviewModal — mirrors the worker's ProposeClosedLoanDirective. */
export interface ProposeClosedLoanDirective {
  type: "propose_closed_loan_update";
  deal_id: string;
  deal_name: string;
  fields: {
    final_loan_amount?: ClosedLoanDirectiveField;
    final_rate?: ClosedLoanDirectiveField;
    final_term_months?: ClosedLoanDirectiveField;
    position?: ClosedLoanDirectiveField;
    payment_amount?: ClosedLoanDirectiveField;
    close_date?: ClosedLoanDirectiveField;
    loan_due_date?: ClosedLoanDirectiveField;
    commission_amount?: ClosedLoanDirectiveField;
  };
  lender?: {
    extracted_name: string;
    matched_id: string | null;
    matched_name: string | null;
    confidence: number;
    source: string;
  };
  lender_candidates: Array<{ id: string; name: string }>;
  current: {
    final_loan_amount: number | null;
    final_rate: number | null;
    final_term_months: number | null;
    position: string | null;
    lender_name: string | null;
    lender_id: string | null;
    payment_amount: number | null;
    close_date: string | null;
    loan_due_date: string | null;
    commission_amount: number | null;
    referral_fee_paid: number | null;
  };
}

export interface ClosedLoanUpdateBody {
  final_loan_amount?: number | null;
  final_rate?: number | null;
  final_term_months?: number | null;
  position?: string | null;
  lender_id?: string | null;
  payment_amount?: number | null;
  close_date?: string | null;
  loan_due_date?: string | null;
  commission_amount?: number | null;
  referral_fee_paid?: number | null;
}
/** Write user-confirmed closed-loan details. Zoho recomputes Final_Commision. */
export const updateClosedLoan = (dealId: string, body: ClosedLoanUpdateBody) =>
  api<{ ok: true; deal_id: string; written: string[] }>(
    `/api/closed-loan/${encodeURIComponent(dealId)}/update`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

export interface ChatTurnResponse {
  text: string;
  tool_calls: { name: string; input: unknown }[];
  iterations: number;
  /** UI side-channel from the chat brain — e.g. navigate the SPA to a deal. */
  directive?: ChatDirective | null;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
  };
}

// ============================================================
// Doc Portal (Phase 1 — read-only proxy)
// ============================================================
import type {
  PortalState,
  ChecklistResponse,
  PortalEvent,
  PortalContact,
  SendPortalRequest,
  SendPortalResponse,
  DocActionResponse,
  RevokePortalResponse,
  TemplatesResponse,
} from "./types/portal";

/**
 * Returns the portal state for a deal, or `null` if no portal has been sent
 * yet (worker maps the portal's 404 to `{ error: "no_portal" }`).
 */
export const fetchPortalForDeal = async (
  dealId: string,
): Promise<PortalState | null> => {
  try {
    return await api<PortalState>(
      `/api/portal/deal/${encodeURIComponent(dealId)}`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404 && err.code === "no_portal") {
      return null;
    }
    throw err;
  }
};

export const fetchPortalChecklist = () =>
  api<ChecklistResponse>("/api/portal/checklist");

export const fetchPortalTemplates = () =>
  api<TemplatesResponse>("/api/portal/templates");

export const fetchPortalEvents = (dealId: string, limit = 50) =>
  api<{ events: PortalEvent[]; next_cursor?: string | null }>(
    `/api/portal/events/${encodeURIComponent(dealId)}?limit=${limit}`,
  );

/**
 * Zoho contact lookup for Send modal autofill. Returns 404 with `no_contact`
 * code when the deal has no linked primary contact in Zoho — caller should
 * render empty fields in that case (don't surface the error).
 */
export const fetchPortalDealContact = async (
  dealId: string,
): Promise<PortalContact | null> => {
  try {
    return await api<PortalContact>(
      `/api/portal/deal/${encodeURIComponent(dealId)}/contact`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404 && err.code === "no_contact") {
      return null;
    }
    throw err;
  }
};

// ─── Write actions (Phase 3) ──────────────────────────────────────────────
// Idempotency keys are generated by the calling component (crypto.randomUUID())
// and persisted across retries within the same user action — never regenerated
// on retry, so duplicate clicks/network retries dedupe at Portal (24h TTL).

function postJsonWithIdempotency<T>(
  path: string,
  body: unknown,
  idempotencyKey: string,
): Promise<T> {
  return api<T>(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
}

export const sendPortal = (
  dealId: string,
  body: SendPortalRequest,
  idempotencyKey: string,
) =>
  postJsonWithIdempotency<SendPortalResponse>(
    `/api/portal/deal/${encodeURIComponent(dealId)}/send`,
    body,
    idempotencyKey,
  );

export const approveDocPortal = (
  dealId: string,
  docKey: string,
  idempotencyKey: string,
) =>
  postJsonWithIdempotency<DocActionResponse>(
    `/api/portal/deal/${encodeURIComponent(dealId)}/action`,
    { doc_key: docKey, action: "approve" },
    idempotencyKey,
  );

export const reviseDocPortal = (
  dealId: string,
  docKey: string,
  revisionNote: string,
  idempotencyKey: string,
) =>
  postJsonWithIdempotency<DocActionResponse>(
    `/api/portal/deal/${encodeURIComponent(dealId)}/action`,
    { doc_key: docKey, action: "revise", revision_note: revisionNote },
    idempotencyKey,
  );

export const revokePortal = (
  dealId: string,
  reason: string | undefined,
  idempotencyKey: string,
) =>
  postJsonWithIdempotency<RevokePortalResponse>(
    `/api/portal/deal/${encodeURIComponent(dealId)}/revoke`,
    reason ? { reason } : {},
    idempotencyKey,
  );

// ============================================================
// LME (Lender Match Engine) — Phase A read-side
// ============================================================
import type {
  DraftEmailResponse,
  ExtractScenarioResponse,
  FinalizeDraftResponse,
  FinalizeResponse,
  LogPitchEntry,
  LogPitchResponse,
  MatchResponse,
  PersistFinalizeResponse,
  PersonalizeIntroResponse,
  Scenario,
  SendEmailResponse,
  StateResponse,
  SyncScenarioResponse,
  ChatTurn,
  FinalizedEmail,
  UpdateDealScenarioRequest,
  UpdateDealScenarioResponse,
} from "./types/lme";

export const fetchLmeMatch = (args: {
  deal_id: string;
  scenario_overrides?: Partial<Scenario>;
  include_disqualified?: boolean;
}) =>
  api<MatchResponse>("/api/lme/match", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });

export const fetchLmeState = (dealId: string) =>
  api<StateResponse>(`/api/lme/state/${encodeURIComponent(dealId)}`);

export const fetchLmeDraftEmail = (args: {
  deal_id: string;
  lender_account_id: string;
  intro_paragraph_override?: string | null;
  finalized_email_json?: FinalizedEmail | null;
}) =>
  api<DraftEmailResponse>("/api/lme/draft-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });

export const fetchLmePersonalizeIntro = (args: {
  deal_id: string;
  lender_account_id: string;
  finalized_email_json?: FinalizedEmail | null;
  /** Phase B.2: when present and non-empty, worker rewrites this draft to be
   *  more professional (preserving user intent) instead of generating fresh. */
  current_intro?: string | null;
}) =>
  api<PersonalizeIntroResponse>("/api/lme/personalize-intro", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });

export const callLmeFinalize = (args: {
  deal_id: string;
  scenario_overrides?: Partial<Scenario>;
  selected_lender_ids?: string[];
  chat_history?: ChatTurn[];
  revision_request?: string | null;
  /** LME-2a — section key ("NARRATIVE") or "ALL"; restricts the edit. */
  revision_target?: string | null;
  /** LME-2a — the email currently shown; the revision merge baseline. */
  base_email?: FinalizedEmail;
}) =>
  api<FinalizeResponse>("/api/lme/finalize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });

// Phase B
export const fetchLmeFinalizeDraft = (dealId: string) =>
  api<FinalizeDraftResponse>(
    `/api/lme/finalize-draft/${encodeURIComponent(dealId)}`,
  );

/** Phase B.2 — discard any KV draft + the Zoho-finalized restore path. Used by
 *  the "Start from scratch" footer link in LmeFinalizeModal. The DELETE only
 *  clears KV; the Zoho-finalized restore path is bypassed by the SPA calling
 *  /finalize directly afterwards. */
export const deleteLmeFinalizeDraft = (dealId: string) =>
  api<{ ok: true; deal_id: string; cleared: true }>(
    `/api/lme/finalize-draft/${encodeURIComponent(dealId)}`,
    { method: "DELETE" },
  );

/** Phase B.2 — write any subset of the 6 required scenario fields to Zoho. */
export const updateLmeDealScenario = (body: UpdateDealScenarioRequest) =>
  api<UpdateDealScenarioResponse>("/api/lme/update-deal-scenario", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/** Phase B.2 — ask Claude to extract the 6 scenario fields from Outlook
 *  emails + Zoho Notes for the borrower on this deal. */
export const extractLmeScenarioFromContext = (dealId: string) =>
  api<ExtractScenarioResponse>("/api/lme/extract-scenario-from-context", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deal_id: dealId }),
  });

/** Phase B.3 — extract writable scenario fields from the Q&A conversation and
 *  write high-confidence ones back to the Zoho Deal. Fired on Q&A submit. */
export const syncLmeScenario = (dealId: string, chatHistory: ChatTurn[]) =>
  api<SyncScenarioResponse>("/api/lme/sync-scenario", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deal_id: dealId, chat_history: chatHistory }),
  });

export const persistLmeFinalize = (
  dealId: string,
  email: {
    subject: string;
    intro: string;
    kpi_boxes: Array<{ value: string; label: string; sub: string }>;
    property_rows: Array<{ key: string; value: string }>;
    borrower_rows: Array<{ key: string; value: string }>;
    use_rows: Array<{ key: string; value: string }>;
    narrative: string;
    flags: string[];
    finalized_at: string;
  },
  idempotencyKey: string,
) =>
  api<PersistFinalizeResponse>("/api/lme/persist-finalize", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({ deal_id: dealId, email }),
  });

export const logLmePitch = (dealId: string, pitches: LogPitchEntry[]) =>
  api<LogPitchResponse>("/api/lme/log-pitch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deal_id: dealId, pitches }),
  });

/** Phase C — send a per-lender pitch email via Microsoft Graph. `to` is sent
 *  verbatim (lender primary contact email); worker BCCs the Zoho dropbox. */
export const sendLmeEmail = (args: {
  deal_id: string;
  lender_account_id: string;
  to: string;
  cc?: string[];
  subject: string;
  body_html: string;
  body_plain?: string;
}) =>
  api<SendEmailResponse>("/api/lme/send-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });

/** No-op placeholder for FinalizedEmail import; type is also used by SPA */
export type _LmeFinalizedEmail = FinalizedEmail;

// ============================================================
// Deal Intake (Phase 3 — Surface A: cockpit Create)
// ============================================================
import type {
  DealPicklists,
  CreatePayload,
  EnrichPayload,
  ExtractionResult,
  IntakeChatMessage,
} from "./types/intake";

export const fetchIntakePicklists = () =>
  api<DealPicklists>("/api/intake/picklists");

/** Current Zoho field values (by API name) for the Enrich conflict detection. */
export const fetchIntakeDealCurrent = (dealId: string) =>
  api<{ current: Record<string, unknown> }>(
    `/api/intake/deal/${encodeURIComponent(dealId)}/current`,
  );

/** Scoped intake conversation turn. Send the running message array; the worker
 *  runs the loop (incl. Outlook tools) and returns the updated array + reply.
 *  `dealContext` (enrich tab) makes the conversation aware of the existing deal. */
export const intakeChat = (messages: IntakeChatMessage[], dealContext?: string) =>
  api<{ messages: IntakeChatMessage[]; assistant_text: string }>("/api/intake/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, deal_context: dealContext }),
  });

/** Transcribe one audio segment via the Worker's Whisper proxy. Returns text. */
export const transcribeIntakeAudio = async (blob: Blob, filename: string): Promise<string> => {
  const headers = new Headers();
  if (ADMIN_TOKEN) headers.set("Authorization", `Bearer ${ADMIN_TOKEN}`);
  const form = new FormData();
  form.append("file", blob, filename);
  const res = await fetch(apiUrl("/api/intake/transcribe"), { method: "POST", headers, body: form });
  if (!res.ok) {
    let code = "transcribe_failed";
    try {
      const b = (await res.json()) as { error?: string };
      if (b?.error) code = b.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, code, null);
  }
  const j = (await res.json()) as { transcript: string };
  return j.transcript;
};

/** Extract structured fields + note from a photo of notes / a filled intake sheet. */
export const extractIntakeImage = (
  image_base64: string,
  media_type: string,
  note_variant?: "intake" | "call",
) =>
  api<ExtractionResult>("/api/intake/extract-image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image_base64, media_type, note_variant }),
  });

/** Extract structured fields + note from pasted text / audio transcript /
 *  serialized chat transcript (tool-use forced server-side). */
export const extractIntakeText = (
  text: string,
  source: "text" | "audio" | "chat" = "text",
  note_variant?: "intake" | "call",
) =>
  api<ExtractionResult>("/api/intake/extract-text", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, source, note_variant }),
  });

export interface CreateDealResponse {
  ok: true;
  deal_id: string;
  deal_name: string;
  note_id: string | null;
  /** true = saved, false = requested-but-failed, null = none requested. */
  note_saved: boolean | null;
  note_error: string | null;
  /** Borrower contact link outcome (non-fatal — deal creates regardless). */
  contact_id: string | null;
  contact_linked: boolean;
  contact_error: string | null;
}
export const createIntakeDeal = (body: CreatePayload) =>
  api<CreateDealResponse>("/api/intake/create-deal", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export interface EnrichDealResponse {
  ok: true;
  deal_id: string;
  updated_fields: string[];
  loan_request_notes_written: boolean;
  call_note_id: string | null;
  call_note_saved: boolean | null;
  call_note_error: string | null;
}
export const enrichIntakeDeal = (body: EnrichPayload) =>
  api<EnrichDealResponse>("/api/intake/enrich-deal", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/** Retry a failed intake/call note write (the loud-failure retry path). */
export const saveIntakeNote = (body: {
  deal_id: string;
  kind: "intake" | "call";
  content: string;
}) =>
  api<{ ok: true; note_id: string }>("/api/intake/save-note", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export function postChat(args: {
  message: string;
  /** Per-tab session id — required so the worker can thread conversation memory. */
  session_id: string;
  context?: { view?: string; deal_id?: string; deal_name?: string };
}) {
  return api<ChatTurnResponse>("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
}
